import React from "react";
import { t } from "ttag";

import { IProduct } from "../../../models/catalogue";
import { BreakPoint } from "../../../models/screen";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { defaults } from "../defaults";
import { SideNav } from "../elements/SideNav";
import { GridFilterConfig } from "../filters";
import { GridVariant } from "../models";
import { updateSelectManyFilterValue, updateSortMethod } from "../reducers";
import { IReduxState } from "../reducers.interfaces";
import { GridSorterConfig } from "../sorters";

import styles from "./TopNav.module.scss";

const isMobileOrTabletWidth = (): boolean => {
    const currentBreakpoint = getViewportBreakpoint();
    return currentBreakpoint < BreakPoint.LARGE;
};

const buildButtons = (
    filters: GridFilterConfig[],
    products: ReadonlyArray<IProduct>,
    filterState: IReduxState["filters"],
    onButtonClick: (
        filterID: string,
        optionID: string,
        event: React.MouseEvent<HTMLButtonElement>,
    ) => void,
): React.JSX.Element[] => {
    const buttons: React.JSX.Element[] = [];

    for (const filterConfig of filters) {
        const options = filterConfig.listFilterOptions(products);
        const filterStateData = filterState[filterConfig.filterID];
        const selectedValues = new Set(
            filterStateData ? filterStateData.selectedValues : [],
        );
        for (const option of options) {
            if (selectedValues.has(option.id)) {
                buttons.push(
                    <button
                        key={`${filterConfig.filterID}-${option.id}`}
                        className={styles.filterTag}
                        onClick={(e) =>
                            onButtonClick(filterConfig.filterID, option.id, e)
                        }
                    >
                        <span className="ada-screenreader-only">
                            Remove filter
                        </span>
                        {option.label}
                    </button>,
                );
            }
        }
    }

    return buttons;
};

const buildFilters = (
    filters: GridFilterConfig[],
    products: ReadonlyArray<IProduct>,
    filterState: IReduxState["filters"],
    onButtonClick: (
        filterID: string,
        optionID: string,
        event: React.MouseEvent<HTMLButtonElement>,
    ) => void,
    onButtonClear: (event: React.MouseEvent<HTMLButtonElement>) => void,
): React.JSX.Element => {
    const buttons = buildButtons(filters, products, filterState, onButtonClick);
    return (
        <div className={styles.tagContainer}>
            {buttons}
            {buttons.length > 1 && (
                <button className={styles.clearButton} onClick={onButtonClear}>
                    Clear All
                </button>
            )}
        </div>
    );
};

const buildFilterMenu = (
    gridVariant: GridVariant,
    filters: GridFilterConfig[],
): React.JSX.Element => {
    return (
        <div className={styles.filterContainer}>
            <SideNav gridVariant={gridVariant} filters={filters} />
        </div>
    );
};

const buildSortMenu = (
    sorters: GridSorterConfig[] | undefined,
    hideSort: boolean | undefined,
    sortMethod: string | null,
    onChangeSortMethod: (sortMethod: string) => void,
): React.JSX.Element | null => {
    if (!sorters || hideSort) {
        return null;
    }

    return (
        <div className={styles.sortContainer}>
            <label htmlFor="sorter" className={styles.sortLabel}>
                {t`Sort by:`}
            </label>
            <select
                id="sorter"
                className={styles.sortSelect}
                value={sortMethod || sorters[0].id}
                onChange={(e) => {
                    onChangeSortMethod(e.currentTarget.value);
                }}
            >
                {sorters.map((sorter) => (
                    <option key={sorter.id} value={sorter.id}>
                        {sorter.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

export const TopNavComponent = (props: {
    gridVariant: GridVariant;
    filters: GridFilterConfig[];
    sorters?: GridSorterConfig[];
    hideSort?: boolean;
    products: ReadonlyArray<IProduct>;
    filterState: IReduxState["filters"];
    sortMethod: string | null;
    onButtonClick: (
        optionNamespace: string,
        filterID: string,
        optionID: string,
    ) => void;
    onChangeSortMethod: (sortMethod: string) => void;
}): React.ReactNode => {
    const handleButtonClick = (
        filterID: string,
        optionID: string,
        event: React.MouseEvent<HTMLButtonElement>,
    ): void => {
        event.preventDefault();
        const optionNamespace = props.products[0]?.product_class_slug || "";
        props.onButtonClick(optionNamespace, filterID, optionID);
    };

    const handleButtonClear = (
        event: React.MouseEvent<HTMLButtonElement>,
    ): void => {
        event.preventDefault();
        const optionNamespace = props.products[0]?.product_class_slug || "";
        for (const filterConfig of props.filters) {
            const options = filterConfig.listFilterOptions(props.products);
            const filterStateData = props.filterState[filterConfig.filterID];
            const selectedValues = new Set(
                filterStateData ? filterStateData.selectedValues : [],
            );
            for (const option of options) {
                if (selectedValues.has(option.id)) {
                    props.onButtonClick(
                        optionNamespace,
                        filterConfig.filterID,
                        option.id,
                    );
                }
            }
        }
    };

    const handleChangeSortMethod = (newSortMethod: string): void => {
        props.onChangeSortMethod(newSortMethod);
    };

    // TODO: Need figure out how to handle the default sort
    // this following code is commented out so that any sort method is not applied on page load.
    // useEffect(() => {
    //     if (props.sorters && !props.sortMethod) {
    //         handleChangeSortMethod(props.sorters[0].id);
    //     }
    // }, []);

    if (isMobileOrTabletWidth()) {
        return (
            <div className={styles.container}>
                <div className={styles.mobileWrapper}>
                    <div className={styles.mobileFilterWrapper}>
                        {buildFilterMenu(props.gridVariant, props.filters)}
                    </div>
                    <div className={styles.mobileSortWrapper}>
                        {buildSortMenu(
                            props.sorters,
                            props.hideSort,
                            props.sortMethod,
                            handleChangeSortMethod,
                        )}
                    </div>
                </div>
                {buildFilters(
                    props.filters,
                    props.products,
                    props.filterState,
                    handleButtonClick,
                    handleButtonClear,
                )}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {buildFilters(
                props.filters,
                props.products,
                props.filterState,
                handleButtonClick,
                handleButtonClear,
            )}
            {buildSortMenu(
                props.sorters,
                props.hideSort,
                props.sortMethod,
                handleChangeSortMethod,
            )}
        </div>
    );
};

export const TopNav = React.memo(
    (props: {
        gridVariant: GridVariant;
        filters: GridFilterConfig[];
        sorters?: GridSorterConfig[];
        hideSort?: boolean;
    }): React.ReactNode => {
        const dispatch = useAppDispatch();
        const products = useAppSelector(
            (state) => state.productgrid2?.products || defaults.products,
        );
        const filterState = useAppSelector(
            (state) => state.productgrid2?.filters || defaults.filters,
        );
        const sortMethod = useAppSelector(
            (state) => state.productgrid2?.sortMethod || defaults.sortMethod,
        );

        const handleButtonClick = (
            optionNamespace: string,
            filterID: string,
            optionID: string,
        ): void => {
            dispatch(
                updateSelectManyFilterValue({
                    optionNamespace: optionNamespace,
                    filterID: filterID,
                    optionID: optionID,
                    isSelected: false,
                }),
            );
        };

        const handleChangeSortMethod = (newSortMethod: string): void => {
            dispatch(updateSortMethod(newSortMethod));
        };

        return (
            <TopNavComponent
                gridVariant={props.gridVariant}
                filters={props.filters}
                sorters={props.sorters}
                hideSort={props.hideSort}
                products={products}
                filterState={filterState}
                sortMethod={sortMethod}
                onButtonClick={handleButtonClick}
                onChangeSortMethod={handleChangeSortMethod}
            />
        );
    },
);
